/*
 * $Id:: DropdownFilters.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';

import { globals as globalsSlice } from '../../app/globalsSlice';
import { t } from '../../providers';
import UAMultiRangePicker from '../UAMultiRangePicker';
import UAMultiFilter from './UAMultiFilter';
import FilterChips from './FilterChips';

const useStyles = makeStyles((theme) => ({
    dropdownFilters: {
        display: 'flex',
        flexFlow: 'row wrap',
        alignItems: 'center',
        '@media (max-width:1100px)': {
            display: 'grid',
            marginBottom: '10px',
        },
        '@media (max-width:600px)': {
            display: 'grid',
            marginBottom: '10px',
            width: '100%',
        },
    },
    filterLabel: {
        fontSize: '0.875rem',
        fontWeight: '600',
        color: theme.palette.text.tableValue,
        margin: '0 0 10px  0',
    },
    dropdownDates: {
        width: '9rem',
        background: 'none',
        marginBottom: '5px',
        marginRight: '0px',
        marginLeft: '0px',
        '& .MuiInputBase-root': {
            '&:focus-visible': {
                backgroundColor: 'rgba(209, 224, 194, 0.2)',
                outline: 'none',
                border: '2px dashed #4F7E1D',
                borderRadius: '3px',
            },
        },
        '@media (max-width:600px)': {
            marginBottom: '2px',
            width: '100%',
        },
        '@media (max-width:1100px)': {
            width: 'auto',
            marginBottom: '20px !important',
        },
    },
}));

const DropdownFilters = ({
    filterItems,
    onUpdateFilters,
    filters,
    applicationType,
    datesOptions,
    filtersChips,
    handleLastFilter,
    handleDateFilter,
    page,
    id = '',
    ariaHidden = false,
}) => {
    const classes = useStyles();
    const [filtersObject, setFiltersObject] = useState([]);
    const [dateRanges, setDateRanges] = useState([]);
    const { filtersOptions } = useSelector(globalsSlice);
    const [rmvDateFilterFromChips, setRmvDateFilterFromChips] = useState('');

    const availableFilterOptions = page !== 'drafts' ? filtersOptions[applicationType] || {} : filtersOptions[page][applicationType] || {};
    const datesFilterOptions = availableFilterOptions[datesOptions] || [];

    const locarnoFilter = (rest) => {
        // Designs Locarno Classes
        let filterOptions = [t('all.classes')];
        const locarnoClasses = availableFilterOptions[rest.name].classification.map((item) => item.classCode);
        filterOptions = filterOptions.concat(locarnoClasses);
        return filterOptions;
    };

    const formTypesFilter = () => {
        let filterOptions = [];
        let ipRT = !!filters.ipRightType && filters.ipRightType.length ? filters.ipRightType : ['Trademarks', 'Designs'];
        if (ipRT.includes('Trademarks')) {
            filterOptions.push(...(availableFilterOptions.tmFilteringTypes || []));
        }
        if (ipRT.includes('Designs')) {
            filterOptions.push(...(availableFilterOptions.dsFilteringTypes || []));
        }
        return filterOptions;
    };

    const filtersRender = (filterItems) => {
        return filterItems.getDropDownFilters(applicationType).map(({ multiple, globalsKey, ...rest }) => {
            const filterValue = filters[rest.name];
            let filterOptions = availableFilterOptions[globalsKey] || rest.filterOptions || [];

            if (applicationType === 'design' && rest.name === 'locarno') {
                filterOptions = locarnoFilter(rest); // Designs Locarno Classes
            } else if (applicationType === 'eservice' && rest.name === 'ipRightType') {
                filterOptions = [{ key: '', label: 'all.ip.types' }].concat(filterOptions); // e-Services IP right Type
            } else if (applicationType === 'eservice' && rest.name === 'formTypes') {
                filterOptions = formTypesFilter(); // e-Services Form Types
            }
            return {
                ...rest,
                multiple,
                value: filterValue,
                filterOptions,
            };
        });
    };

    useEffect(() => {
        const hasDateTypes = availableFilterOptions && availableFilterOptions[datesOptions] && availableFilterOptions[datesOptions].length;
        const newDateRanges = hasDateTypes ? [{ dateType: availableFilterOptions[datesOptions[0]] }] : [];
        setDateRanges(newDateRanges);
    }, [availableFilterOptions, datesOptions]);

    useEffect(() => {
        const filtersToRender = filtersRender(filterItems);

        const { dates } = filters;
        if (dates) {
            setDateRanges(dates);
        } else {
            setDateRanges(null);
        }

        setFiltersObject(filtersToRender);
    }, [setFiltersObject, filters, applicationType]);

    const handleFilterChangeFromChips = (event, optionSelected) => {
        const newValues = event.target.value.filter((val) => val !== optionSelected);
        const idx = filtersObject.findIndex(({ name }) => event.target.name === name);
        if (idx > -1) {
            let isFounded = [];
            filtersObject[idx].filterOptions.forEach((ai) => {
                if (newValues.includes(t(ai.label)) || newValues.includes(ai)) {
                    isFounded.push(ai.key || ai);
                }
            });
            const options = {
                target: {
                    name: event.target.name,
                    value: isFounded,
                },
            };
            handleFilterChange(options, newValues);
        }
    };

    const handleDateFilterChangeFromChips = (dateType, idx, selectedValues) => {
        // if it is not the last filter
        if (selectedValues.length > 1 || !isLastFilterOption('dates')) {
            handleDateFilter(idx);

            //remove dateType from selected dates object
            const newRanges = dateRanges.filter((range) => range.dateType && range.dateType !== dateType);
            setRmvDateFilterFromChips(true);
            // select the first selected Date if exists
            if (!!newRanges.length) {
                handleDateFilterChange(newRanges);
            } else {
                handleDateFilterChange([{ dateType: '', dateTypeLabel: '' }]);
            }
        } else {
            handleLastFilter();
        }
    };

    const handleFilterChange = (event, optionSelected) => {
        if (optionSelected.length > 0 || !isLastFilterOption(event.target.name)) {
            updateFilters(filtersObject, event, optionSelected);
        } else {
            handleLastFilter();
        }
    };

    const updateFilters = (filtersObject, event, optionSelected) => {
        const idx = filtersObject.findIndex(({ name }) => event.target.name === name);
        if (idx > -1) {
            const updatedFilters = [...filtersObject];
            updatedFilters[idx].value = event.target.value;
            setFiltersObject(updatedFilters);

            const updatedValues = { ...filters, [event.target.name]: event.target.value };
            const filterChips = { ...filtersChips, [event.target.name]: optionSelected };

            onUpdateFilters(updatedValues, filterChips);
        }
    };

    const isLastFilterOption = (filterName) => {
        let isLast = true;
        filtersObject.forEach((filter) => {
            if (filter.value && filter.value.length > 0 && filter.name !== filterName) {
                isLast = false;
            }
        });
        return isLast;
    };

    const handleDateFilterChange = (ranges) => {
        if (dateRanges === null) {
            setDateRanges([{ dateType: '', dateTypeLabel: '' }]);
        }
        setDateRanges(ranges);
    };

    const onCloseDatepicker = () => {
        setRmvDateFilterFromChips(false);
        const selectedRanges = dateRanges.filter((item) => item.dateType && item.startDate && item.endDate);
        if (selectedRanges.length > 0) {
            onUpdateFilters({ ...filters, dates: selectedRanges }, { ...filtersChips, dates: selectedRanges });
        } else {
            if (filters.dates && Object.keys(filters.dates).length !== 0) {
                // check previous Filters for dates
                const updatedFilters = { ...filters };
                const updatedFiltersChips = { ...filtersChips };
                delete updatedFilters['dates'];
                delete updatedFiltersChips['dates'];
                onUpdateFilters(updatedFilters, updatedFiltersChips);
            }
        }
    };

    return (
        <React.Fragment>
            <div className={classes.filtersRow} id={id} aria-hidden={ariaHidden} aria-labelledby="accordion-toggle-filters">
                <div className={classes.dropdownFilters}>
                    {/*<InputLabel className={classes.filterLabel}>{t('filters')}</InputLabel>*/}
                    <div className={classes.dropdownFilters}>
                        {(filtersObject || []).map((filter, index) => {
                            return (
                                <React.Fragment key={`${filter.filterid}_${index}`}>
                                    {filter.name === 'dates' ? (
                                        <UAMultiRangePicker
                                            className={classes.dropdownDates}
                                            id={'dateRanges'}
                                            dropdownOptions={datesFilterOptions}
                                            onUpdateRanges={handleDateFilterChange}
                                            onClose={onCloseDatepicker}
                                            ranges={dateRanges}
                                        />
                                    ) : (
                                        <UAMultiFilter filter={filter} handleFilterChange={handleFilterChange} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>
            </div>
            {!!Object.keys(filters || {}).length && (
                <div className={classes.filterTextRow}>
                    <FilterChips
                        filteredBy={filtersChips}
                        onClearFilters={handleLastFilter}
                        onFilterChange={handleFilterChangeFromChips}
                        onDateFilterChange={handleDateFilterChangeFromChips}
                    />
                </div>
            )}
        </React.Fragment>
    );
};

export default DropdownFilters;
